import React from 'react';
import { Modal } from '@ofself/shell';
import { Facts, Note } from '../../components/nucleus/Inspector';
import { Row } from '../../components/nucleus/Collection';
import { dateOf } from '../../nucleus/format';
import { schemaWord, changeRows } from './data/inspector';
import './plugin-inspector.css';

// WHAT A PLUGIN ASKS FOR, said the one way wherever it is read: the store's
// inspector, the set-up one, the setup sheet's Turn on step and "What you
// agreed to". Per tool: Reads (schema · read, or read then deleted), Writes
// (schema · verb), Outside ofSelf (every effect; one that cannot be undone
// says so at its end); then what it asks of every tool, said once: Data goes
// to, asking every run, the request it makes. Schemas by name, never an id.

function Line({ kind, what, end, warn }) {
    return (
        <div className="plugins-asks__line" role="listitem">
            <span className="plugins-asks__kind">{kind}</span>
            <span className="plugins-asks__what">{what}</span>
            {end && <span className={`plugins-asks__end${warn ? ' is-warn' : ''}`}>{end}</span>}
        </div>
    );
}

function toolLines(t, asks, nameOf) {
    const lines = [];
    t.reads.forEach((r) => lines.push({
        key: `r-${r.key}`, kind: 'Reads', what: schemaWord(r, nameOf),
        end: asks.removeSource ? 'read, then deleted' : 'read', warn: asks.removeSource,
    }));
    t.writes.forEach((w, i) => lines.push({ key: `w-${i}`, kind: 'Writes', what: schemaWord(w, nameOf), end: w.verb, warn: w.verb === 'delete' }));
    t.effects.forEach((e, i) => lines.push({
        key: `e-${i}`, kind: 'Outside ofSelf', what: e.description, end: e.reversible ? null : 'Can’t be undone', warn: !e.reversible,
    }));
    return lines;
}

/** It asks for: one block a tool, then what is asked of them all. */
export function AsksFor({ asks, nameOf, toolId = null }) {
    if (!asks) return null;
    const tools = toolId == null ? asks.tools : asks.tools.filter((t) => String(t.id) === String(toolId));
    const many = asks.tools.length > 1;
    const data = asks.data;
    const shared = asks.every.length > 0 || data.here || data.hosts.length > 0 || data.undeclared || !!asks.request || asks.requiresConsent;
    return (
        <div className="plugins-asks">
            {tools.map((t) => {
                const lines = toolLines(t, asks, nameOf);
                return (
                    <div key={t.id || 'tool'} className="plugins-asks__tool">
                        {many && <span className="plugins-asks__name">{t.name}</span>}
                        <div role="list" aria-label={many ? t.name : 'What it does'}>
                            {lines.length
                                ? lines.map(({ key, ...l }) => <Line key={key} {...l} />)
                                : <Line kind="Nothing" what="It reads and writes nothing, and does nothing outside ofSelf" />}
                        </div>
                    </div>
                );
            })}
            {shared && (
            <div className="plugins-asks__tool">
                {many && <span className="plugins-asks__name">Every tool</span>}
                <div role="list" aria-label="Every tool">
                    {asks.every.map((r) => <Line key={`every-${r.key}`} kind="Reads" what={schemaWord(r, nameOf)} end={asks.removeSource ? 'read, then deleted' : 'read'} warn={asks.removeSource} />)}
                    {data.here && <Line kind="Data goes to" what="Nowhere: it runs on ofSelf" />}
                    {!data.here && data.hosts.map((h) => <Line key={h} kind="Data goes to" what={h} />)}
                    {!data.here && data.undeclared && <Line kind="Data goes to" what="A place it does not name" end="treat as outside ofSelf" warn />}
                    {asks.request && <Line kind="Request" what={`${asks.request.method} ${asks.request.path}`} />}
                    {asks.requiresConsent && <Line kind="Each run" what="Asks you first, every time" />}
                </div>
            </div>
            )}
        </div>
    );
}

/** What changed since it was approved: Adds · ‹what› (marked) and No longer · ‹what›. */
export function ChangeRows({ approval, tools }) {
    const rows = changeRows(approval, tools);
    if (!rows.length) return <Note>Its developer changed it, so it needs you to turn it on again.</Note>;
    return rows.map((r) => (
        <Row key={r.key} name={`${r.word} · ${r.what}`} sub={r.sub || undefined} end={r.warn ? 'new' : undefined} endTone={r.warn ? 'warn' : undefined} />
    ));
}

/** Turned on ‹date›, for an earlier version — or Not yet. */
export function approvedWord(approval, needsApproval) {
    if (!approval?.approvedAt) return needsApproval ? 'Not yet' : 'Yes';
    return approval.changed ? 'For an earlier version' : dateOf(approval.approvedAt);
}

/** "What you agreed to": what it could do when it was turned on, read-only. */
export function ApprovedModal({ name, approval, asks, nameOf, onClose }) {
    return (
        <Modal open onClose={onClose} title={`What you agreed to for ${name}`} size="md">
            <div className="plugins-form">
                <Facts items={[
                    { label: 'Turned on', value: approval?.approvedAt ? dateOf(approval.approvedAt) : 'Not yet' },
                    approval?.approvedBy && { label: 'By', value: approval.approvedBy },
                    approval?.approvedVersion != null && { label: 'Version', value: approval.changed && approval.currentVersion != null ? `${approval.approvedVersion} (now ${approval.currentVersion})` : String(approval.approvedVersion) },
                ]} />
                {approval?.changed && (
                    <>
                        <span className="plugins-sub">Since you turned it on</span>
                        <ChangeRows approval={approval} tools={asks?.tools || []} />
                        <span className="plugins-sub">What it asks for now</span>
                    </>
                )}
                <AsksFor asks={asks} nameOf={nameOf} />
            </div>
        </Modal>
    );
}
